import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Upload } from 'lucide-react';
import { useApp } from '@/components/AppLayout';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { businessApi, uploadApi, type IndustryChoice, type CurrencyChoice } from '@/lib/api';

export function BusinessSettingsForm() {
  const { currentBusiness } = useApp();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [industryChoices, setIndustryChoices] = useState<IndustryChoice[]>([]);
  const [currencyChoices, setCurrencyChoices] = useState<CurrencyChoice[]>([]);
  const [name, setName] = useState(currentBusiness?.name || '');
  const [address, setAddress] = useState(currentBusiness?.address || '');
  const [addressCoords, setAddressCoords] = useState<{ latitude: number; longitude: number } | null>(
    currentBusiness?.address_latitude && currentBusiness?.address_longitude
      ? { latitude: currentBusiness.address_latitude, longitude: currentBusiness.address_longitude }
      : null
  );
  const [industry, setIndustry] = useState(currentBusiness?.industry || '');
  const [industryOther, setIndustryOther] = useState(currentBusiness?.industry_other || '');
  const [defaultCurrency, setDefaultCurrency] = useState(currentBusiness?.default_currency || '$');
  const [logo, setLogo] = useState(currentBusiness?.logo || '');
  const [phone, setPhone] = useState(currentBusiness?.phone || '');
  const [website, setWebsite] = useState(currentBusiness?.website || '');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(currentBusiness?.logo || null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Load industry choices
    businessApi.getIndustries().then((response) => {
      if (response.data) {
        setIndustryChoices(response.data);
      }
    });
    // Load currency choices
    businessApi.getCurrencies().then((response) => {
      if (response.data) {
        setCurrencyChoices(response.data);
      }
    });
  }, []);

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentBusiness) return;

    setError('');
    setSuccess('');
    setSubmitting(true);

    // Validate
    if (!name.trim()) {
      setError('Business name is required');
      setSubmitting(false);
      return;
    }
    if (!address.trim()) {
      setError('Business address is required');
      setSubmitting(false);
      return;
    }
    if (industry === 'other' && !industryOther.trim()) {
      setError('Please specify your industry');
      setSubmitting(false);
      return;
    }

    // Upload logo first if there's a pending file
    let finalLogoUrl = logo;
    if (logoFile) {
      const uploadResponse = await uploadApi.uploadLogo(logoFile);
      if (uploadResponse.error) {
        setError(uploadResponse.error);
        setSubmitting(false);
        return;
      }
      if (uploadResponse.data) {
        finalLogoUrl = uploadResponse.data.url;
      }
    }

    const data = {
      name: name.trim(),
      address: address.trim(),
      address_latitude: addressCoords?.latitude,
      address_longitude: addressCoords?.longitude,
      industry,
      industry_other: industry === 'other' ? industryOther : '',
      default_currency: defaultCurrency,
      logo: finalLogoUrl,
      phone: phone.trim(),
      website: website.trim(),
    };

    const response = await businessApi.update(currentBusiness.id, data);

    setSubmitting(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    setSuccess('Business information updated successfully');
    setLogoFile(null);

    // Refresh user data to get updated business
    window.location.reload();
  };

  const handleCancel = () => {
    if (!currentBusiness) return;

    // Reset form to current business data
    setName(currentBusiness.name || '');
    setAddress(currentBusiness.address || '');
    setAddressCoords(
      currentBusiness.address_latitude && currentBusiness.address_longitude
        ? { latitude: currentBusiness.address_latitude, longitude: currentBusiness.address_longitude }
        : null
    );
    setIndustry(currentBusiness.industry || '');
    setIndustryOther(currentBusiness.industry_other || '');
    setDefaultCurrency(currentBusiness.default_currency || '$');
    setLogo(currentBusiness.logo || '');
    setPhone(currentBusiness.phone || '');
    setWebsite(currentBusiness.website || '');
    setLogoPreview(currentBusiness.logo || null);
    setLogoFile(null);
    setError('');
    setSuccess('');
  };

  if (!currentBusiness) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Business Information</CardTitle>
        <CardDescription>
          Manage your business profile and settings.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Separator className="mb-6" />
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Logo Upload */}
          <div className="space-y-2">
            <Label>Business Logo</Label>
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                {logoPreview ? (
                  <AvatarImage src={logoPreview} alt="Business logo" />
                ) : (
                  <AvatarFallback className="text-lg font-semibold">
                    {currentBusiness.initials}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoSelect}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitting}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {logoFile ? 'Change Image' : 'Choose Image'}
                </Button>
                {logoFile && (
                  <p className="text-xs text-muted-foreground">
                    New logo selected. Click "Save Changes" to upload.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Business Name */}
          <div className="space-y-2">
            <Label htmlFor="business-name">Business Name</Label>
            <Input
              id="business-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your business name"
              required
            />
          </div>

          {/* Business Address */}
          <div className="space-y-2">
            <Label htmlFor="business-address">Business Address</Label>
            <AddressAutocomplete
              id="business-address"
              value={address}
              onChange={(value, coords) => {
                setAddress(value);
                if (coords) {
                  setAddressCoords(coords);
                }
              }}
              placeholder="Start typing your business address..."
              rows={3}
              required
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="business-phone">Phone</Label>
            <Input
              id="business-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Website */}
          <div className="space-y-2">
            <Label htmlFor="business-website">Website</Label>
            <Input
              id="business-website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="www.example.com"
            />
          </div>

          {/* Industry Select */}
          <div className="space-y-2">
            <Label htmlFor="industry">Industry</Label>
            <Select value={industry || '_none'} onValueChange={(val) => setIndustry(val === '_none' ? '' : val)}>
              <SelectTrigger id="industry">
                <SelectValue placeholder="Select an industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {industryChoices.map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Industry Other (shown only when industry="other") */}
          {industry === 'other' && (
            <div className="space-y-2">
              <Label htmlFor="industry-other">Specify Industry</Label>
              <Input
                id="industry-other"
                value={industryOther}
                onChange={(e) => setIndustryOther(e.target.value)}
                placeholder="e.g. Landscaping, HVAC, etc."
                required
              />
            </div>
          )}

          {/* Default Currency */}
          <div className="space-y-2">
            <Label htmlFor="default-currency">Default Currency</Label>
            <Select value={defaultCurrency} onValueChange={setDefaultCurrency}>
              <SelectTrigger id="default-currency">
                <SelectValue placeholder="Select a currency" />
              </SelectTrigger>
              <SelectContent>
                {currencyChoices.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              This currency symbol will appear on all your invoices and quotes.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3">
              <p className="text-sm text-red-900 dark:text-red-100">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md p-3">
              <p className="text-sm text-green-900 dark:text-green-100">{success}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button type="button" variant="outline" onClick={handleCancel} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
